import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../api/axios";
import Modal from "../../components/common/AdminModal";
import Toast from "../../components/common/Toast";
import AdminPageHeader from "../../components/common/AdminPageHeader";
import { useMainCompany } from "../../context/MainCompanyContext";
import { useRoleTheme } from "../../context/RoleThemeContext";
import {
  Eye,
  Pencil,
  Trash,
  KeyRound,
  Power,
  CheckCircle,
  XCircle,
  Mail,
  UserPlus,
  Camera,
  Settings2,
  RefreshCw,
  Users,
  MapPin,
  AtSign,
  Phone,
  Copy,
  Check,
  FileSpreadsheet,
  ArrowUp,
  ArrowDown,
  ChevronsUpDown,
} from "lucide-react";

// Location names come back from the DB in ALL CAPS (e.g. "LOS ANGELES") — display
// them Title Case instead. Not used on company_name, which already arrives correctly
// cased (and can contain abbreviations like "CA" that this would mangle).
function toTitleCase(str) {
  if (!str) return str;
  return String(str)
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function CopyButton({ value, label }) {
  const [copied, setCopied] = useState(false);
  if (!value) return null;
  const handleCopy = async (e) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch {
      // clipboard permission denied/unavailable — nothing else to fall back to
    }
  };
  return (
    <button
      type="button"
      onClick={handleCopy}
      title={`Copy ${label}`}
      className="text-gray-300 hover:text-gray-600 shrink-0"
    >
      {copied ? <Check className="w-3 h-3 text-green-600" /> : <Copy className="w-3 h-3" />}
    </button>
  );
}

function SortHeader({ field, label, sortBy, sortOrder, onSort }) {
  const active = sortBy === field;
  return (
    <button
      type="button"
      onClick={() => onSort(field)}
      className={`inline-flex items-center gap-1 hover:text-gray-900 ${active ? "text-gray-900" : ""}`}
    >
      {label}
      {active ? (
        sortOrder === "asc" ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
      ) : (
        <ChevronsUpDown className="w-3 h-3 text-gray-400" />
      )}
    </button>
  );
}

function initials(name) {
  const parts = String(name || "").trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "?";
  return (parts[0][0] + (parts[1]?.[0] || "")).toUpperCase();
}

/**
 * Read-only-by-default combined view of Users + Sales Reps (GET /admin/users/combined).
 * Simple, single-call actions (toggle_status/delete/reset_password/approve/reject/
 * send_welcome_email) are handled directly here. Anything that needs the full
 * edit form (edit/view/assign_sales_rep for users; edit/view/toggle_fields/
 * upload_image for sales reps) deep-links to the existing, battle-tested pages:
 *  - Users:      /admin/users?userId=<id>        (pre-filters that page's list —
 *                pre-existing mechanism, no changes made to AdminUsers.js)
 *  - Sales Reps: /admin/sales-reps?repId=<id>     (opens that page's edit modal —
 *                small additive deep link added to AdminSalesReps.js)
 * Neither existing page's own routes/behavior changes when these params are absent.
 */

const KIND_OPTIONS = [
  { value: "all", label: "All" },
  { value: "user", label: "Users" },
  { value: "sales_rep", label: "Sales Reps" },
];

const USER_TYPE_LABEL = {
  N: "Normal User",
  D: "Disabled",
  M: "Master Account",
  S: "Sales Rep",
  SM: "Sales Rep Manager",
};

// Mirrors the backend's kind-driven user_type groups — since `user_type` ANDs on
// top of `kind` (not a replacement), the dropdown must only offer types that are
// actually valid for the selected kind, or the two filters can silently contradict
// each other and return zero rows (e.g. kind=user + user_type=S).
const USER_TYPES_FOR_KIND = {
  all: ["N", "D", "M", "S", "SM"],
  user: ["N", "D", "M"],
  sales_rep: ["S", "SM"],
};

function TypeBadge({ userType, kind }) {
  const label = USER_TYPE_LABEL[userType] || userType || "—";
  const colors =
    kind === "sales_rep"
      ? "bg-purple-50 text-purple-700 border-purple-200"
      : userType === "M"
        ? "bg-amber-50 text-amber-700 border-amber-200"
        : "bg-blue-50 text-blue-700 border-blue-200";
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${colors}`}>
      {label}
    </span>
  );
}

function StatusBadge({ isActive }) {
  return isActive ? (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-50 text-green-700 border border-green-200">
      Active
    </span>
  ) : (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600 border border-gray-200">
      Inactive
    </span>
  );
}

export default function AdminUsersSalesRepsCombined() {
  const { selectedMain } = useMainCompany();
  const { roleTheme } = useRoleTheme();
  const primaryBtnClasses = `${roleTheme?.colors?.buttons?.primary || "bg-blue-600 hover:bg-blue-700"} text-white`;
  const navigate = useNavigate();

  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ total: 0, page: 1, limit: 10, totalPages: 1 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [states, setStates] = useState([]);
  const stateNameById = useMemo(() => {
    const map = {};
    states.forEach((s) => {
      map[String(s.id ?? s.stateid ?? s.value)] = toTitleCase(s.name ?? s.statename ?? s.label);
    });
    return map;
  }, [states]);

  // filters (only applied to the fetch when Search/Reset is clicked, or page/kind change)
  const [searchInput, setSearchInput] = useState("");
  const [appliedQuery, setAppliedQuery] = useState("");
  const [kind, setKind] = useState("all");
  const [userTypeFilter, setUserTypeFilter] = useState("");
  const [stateFilter, setStateFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState(""); // '', 'true', 'false'
  const [createdFrom, setCreatedFrom] = useState("");
  const [createdTo, setCreatedTo] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [sortBy, setSortBy] = useState("created");
  const [sortOrder, setSortOrder] = useState("desc");

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder((o) => (o === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(field);
      setSortOrder("asc");
    }
    setPage(1);
  };

  const [toast, setToast] = useState({ visible: false, message: "", type: "success" });
  const showToast = useCallback((message, type = "success", duration = 4000) => {
    setToast({ visible: true, message, type });
    setTimeout(() => setToast((t) => ({ ...t, visible: false })), duration);
  }, []);

  // confirm modal for the simple, single-call actions
  const [confirmModal, setConfirmModal] = useState({ type: null, row: null });
  const [actionBusy, setActionBusy] = useState(false);

  useEffect(() => {
    if (!selectedMain) return;
    let cancelled = false;
    api
      .get("/admin/states", { params: { maincompanyid: selectedMain } })
      .then((res) => {
        if (!cancelled) setStates(res.data?.data || res.data || []);
      })
      .catch(() => {
        if (!cancelled) setStates([]);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedMain]);

  const fetchCombined = useCallback(async () => {
    if (!selectedMain) return;
    try {
      setLoading(true);
      setError("");
      const params = {
        maincompanyid: selectedMain,
        page,
        limit: pageSize,
        kind,
        user_type: userTypeFilter || undefined,
        q: appliedQuery || undefined,
        state: stateFilter || undefined,
        is_active: statusFilter || undefined,
        createdFrom: createdFrom || undefined,
        createdTo: createdTo || undefined,
        sortBy,
        sortOrder,
      };
      const { data } = await api.get("/admin/users/combined", { params });
      setRows(Array.isArray(data?.data) ? data.data : []);
      setMeta(data?.meta || { total: 0, page, limit: pageSize, totalPages: 1 });
    } catch (err) {
      console.error("Failed to load combined list", err);
      setError("Could not load the combined list. Please try again.");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [selectedMain, page, pageSize, kind, userTypeFilter, appliedQuery, stateFilter, statusFilter, createdFrom, createdTo, sortBy, sortOrder]);

  useEffect(() => {
    fetchCombined();
  }, [fetchCombined]);

  const handleSearch = () => {
    setPage(1);
    setAppliedQuery(searchInput.trim());
  };

  const handleReset = () => {
    setSearchInput("");
    setAppliedQuery("");
    setKind("all");
    setUserTypeFilter("");
    setStateFilter("");
    setStatusFilter("");
    setCreatedFrom("");
    setCreatedTo("");
    setPage(1);
  };

  const [exporting, setExporting] = useState(false);
  // Mirrors the JSON-url-then-binary-fallback contract already used by
  // AdminUsers.js's handleExport() against GET /admin/users/export, pointed at
  // the (proposed) combined equivalent. Sends the same filters as the list
  // fetch above, but never page/limit — export should cover every matching row.
  // The backend has, on at least one confirmed production response, still returned
  // data.url as http:// despite the page itself being served over https:// — Chrome
  // silently blocks that download (mixed-content download blocking) with no console
  // error. Upgrading the scheme here is a stop-gap for that backend inconsistency,
  // not a substitute for fixing it server-side.
  const upgradeToHttps = (url) => {
    if (window.location.protocol === "https:" && url?.startsWith("http://")) {
      return "https://" + url.slice("http://".length);
    }
    return url;
  };

  const handleExportCombined = async () => {
    setExporting(true);
    const params = {
      maincompanyid: selectedMain,
      kind,
      user_type: userTypeFilter || undefined,
      q: appliedQuery || undefined,
      state: stateFilter || undefined,
      is_active: statusFilter || undefined,
      createdFrom: createdFrom || undefined,
      createdTo: createdTo || undefined,
    };
    try {
      try {
        const respJson = await api.get("/admin/users/combined/export", { params });
        const d = respJson?.data;
        const url = d?.url || d?.data?.url;
        if (url) {
          window.open(upgradeToHttps(url), "_blank");
          return;
        }
      } catch (e) {
        // server may have returned binary and axios failed to parse it as JSON — fall through
      }

      const resp = await api.get("/admin/users/combined/export", { params, responseType: "arraybuffer" });
      const contentType = resp.headers["content-type"] || "";
      try {
        const text = new TextDecoder("utf-8").decode(resp.data || new Uint8Array());
        const parsed = JSON.parse(text);
        const url = parsed?.url || parsed?.data?.url;
        if (url) {
          window.open(upgradeToHttps(url), "_blank");
          return;
        }
      } catch (e) {
        // not JSON — treat as the binary file itself
      }

      const blob = new Blob([resp.data], { type: contentType || "application/octet-stream" });
      let filename = "accounts_export.xlsx";
      const cd = resp.headers["content-disposition"] || resp.headers["Content-Disposition"];
      if (cd) {
        const m = cd.match(/filename\*?=(?:UTF-8'')?"?([^";]+)/i);
        if (m && m[1]) {
          try { filename = decodeURIComponent(m[1]); } catch { filename = m[1]; }
        }
      }
      const urlObj = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = urlObj;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(urlObj);
    } catch (err) {
      console.error("Export failed", err);
      showToast("Failed to export. Please try again.", "error");
    } finally {
      setExporting(false);
    }
  };

  // ---- navigation-based actions (deep-link to the existing, full-featured pages) ----
  const goToUser = (row, extra = "") => navigate(`/admin/users?userId=${row.id}${extra}`);
  const goToRep = (row) => navigate(`/admin/sales-reps?repId=${row.sales_rep_id}`);

  // ---- direct, single-call actions handled right here ----
  const runAction = async (type, row) => {
    setActionBusy(true);
    try {
      if (type === "toggle_status") {
        await api.post(`/admin/users/${row.id}/toggle-status`, { is_active: !row.is_active });
        showToast(row.is_active ? "Deactivated" : "Activated", "success");
      } else if (type === "delete") {
        if (row.kind === "sales_rep") {
          await api.delete(`/admin/sales-reps/${selectedMain}/${row.sales_rep_id}`);
        } else {
          await api.delete(`/admin/users/${row.id}`);
        }
        showToast("Deleted", "success");
      } else if (type === "reset_password") {
        await api.post(`/admin/users/${row.id}/reset-password`);
        showToast("Password reset email sent", "success");
      } else if (type === "approve") {
        await api.post(`/admin/users/${row.id}/approve`);
        showToast("User approved", "success");
      } else if (type === "reject") {
        await api.post(`/admin/users/${row.id}/reject`);
        showToast("User rejected", "success");
      } else if (type === "send_welcome_email") {
        await api.post(`/admin/users/${row.id}/send-welcome-email`, { email: row.email });
        showToast("Welcome email sent", "success");
      }
      setConfirmModal({ type: null, row: null });
      await fetchCombined();
    } catch (err) {
      console.error(`Action ${type} failed`, err);
      showToast(err?.response?.data?.message || "Action failed. Please try again.", "error");
    } finally {
      setActionBusy(false);
    }
  };

  const CONFIRM_COPY = {
    toggle_status: (row) => (row.is_active ? "Deactivate this account?" : "Activate this account?"),
    delete: () => "Permanently delete this record? This cannot be undone.",
    reset_password: () => "Send a password reset email to this user?",
    approve: () => "Approve this user?",
    reject: () => "Reject this user?",
    send_welcome_email: () => "Resend the welcome email to this user?",
  };

  // action -> icon + click handler, given a row
  const ACTION_DEFS = {
    view: { icon: Eye, label: "View", color: "text-gray-600", onClick: (row) => (row.kind === "sales_rep" ? goToRep(row) : goToUser(row)) },
    edit: { icon: Pencil, label: "Edit", color: "text-green-600", onClick: (row) => (row.kind === "sales_rep" ? goToRep(row) : goToUser(row)) },
    toggle_fields: { icon: Settings2, label: "Fields", color: "text-indigo-600", onClick: (row) => goToRep(row) },
    upload_image: { icon: Camera, label: "Photo", color: "text-purple-600", onClick: (row) => goToRep(row) },
    assign_sales_rep: { icon: UserPlus, label: "Assign Rep", color: "text-teal-600", onClick: (row) => goToUser(row) },
    toggle_status: { icon: Power, label: "Toggle Status", color: "text-orange-600", onClick: (row) => setConfirmModal({ type: "toggle_status", row }) },
    delete: { icon: Trash, label: "Delete", color: "text-red-600", onClick: (row) => setConfirmModal({ type: "delete", row }) },
    reset_password: { icon: KeyRound, label: "Reset Password", color: "text-orange-600", onClick: (row) => setConfirmModal({ type: "reset_password", row }) },
    approve: { icon: CheckCircle, label: "Approve", color: "text-green-600", onClick: (row) => setConfirmModal({ type: "approve", row }) },
    reject: { icon: XCircle, label: "Reject", color: "text-red-600", onClick: (row) => setConfirmModal({ type: "reject", row }) },
    send_welcome_email: { icon: Mail, label: "Resend Welcome Email", color: "text-blue-600", onClick: (row) => setConfirmModal({ type: "send_welcome_email", row }) },
  };

  return (
    <div className="p-4 sm:p-6">
      <AdminPageHeader
        title="All Accounts"
        actions={(
          <div className="flex items-center gap-2">
            <button
              onClick={handleExportCombined}
              disabled={exporting}
              className="inline-flex items-center gap-1.5 px-3 py-2 bg-green-600 hover:bg-green-700 disabled:opacity-60 text-white text-sm font-semibold rounded-lg shadow-sm hover:shadow-md"
            >
              <FileSpreadsheet className="w-4 h-4 shrink-0" />
              <span className="hidden sm:inline">{exporting ? "Exporting…" : "Export Excel"}</span>
            </button>
            <button
              onClick={fetchCombined}
              className={`inline-flex items-center gap-1.5 px-3 py-2 ${primaryBtnClasses} text-sm font-semibold rounded-lg shadow-sm hover:shadow-md`}
            >
              <RefreshCw className="w-4 h-4 shrink-0" />
              <span className="hidden sm:inline">Refresh</span>
            </button>
          </div>
        )}
        filters={(
          <div className="flex flex-nowrap items-end gap-3 overflow-x-auto pb-1">
            <div className="shrink-0">
              <label className="block text-xs font-medium text-gray-600 mb-1">Search</label>
              <input
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                placeholder="Name, email, username, phone…"
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm w-44"
              />
            </div>
            <div className="shrink-0">
              <label className="block text-xs font-medium text-gray-600 mb-1">Type</label>
              <select
                value={kind}
                onChange={(e) => {
                  const nextKind = e.target.value;
                  setKind(nextKind);
                  // Drop the User Type selection if it's no longer valid for this kind,
                  // so the two filters can never contradict each other (kind ANDs user_type).
                  if (userTypeFilter && !USER_TYPES_FOR_KIND[nextKind].includes(userTypeFilter)) {
                    setUserTypeFilter("");
                  }
                  setPage(1);
                }}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
              >
                {KIND_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
            <div className="shrink-0">
              <label className="block text-xs font-medium text-gray-600 mb-1">User Type</label>
              <select
                value={userTypeFilter}
                onChange={(e) => { setUserTypeFilter(e.target.value); setPage(1); }}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
              >
                <option value="">All types</option>
                {Object.entries(USER_TYPE_LABEL)
                  .filter(([value]) => USER_TYPES_FOR_KIND[kind].includes(value))
                  .map(([value, label]) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
              </select>
            </div>
            <div className="shrink-0">
              <label className="block text-xs font-medium text-gray-600 mb-1">State</label>
              <select
                value={stateFilter}
                onChange={(e) => setStateFilter(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
              >
                <option value="">All states</option>
                {states.map((s) => (
                  <option key={s.id ?? s.stateid} value={s.id ?? s.stateid}>
                    {toTitleCase(s.name ?? s.statename)}
                  </option>
                ))}
              </select>
              {/* Note: for sales-rep rows this matches their PRIMARY location only —
                  a rep may still serve additional counties/states not reflected here. */}
            </div>
            <div className="shrink-0">
              <label className="block text-xs font-medium text-gray-600 mb-1">Status</label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
              >
                <option value="">All</option>
                <option value="true">Active</option>
                <option value="false">Inactive</option>
              </select>
            </div>
            <div className="shrink-0">
              <label className="block text-xs font-medium text-gray-600 mb-1">Created From</label>
              <input type="date" value={createdFrom} onChange={(e) => setCreatedFrom(e.target.value)} className="border border-gray-300 rounded-lg px-3 py-2 text-sm w-36" />
            </div>
            <div className="shrink-0">
              <label className="block text-xs font-medium text-gray-600 mb-1">Created To</label>
              <input type="date" value={createdTo} onChange={(e) => setCreatedTo(e.target.value)} className="border border-gray-300 rounded-lg px-3 py-2 text-sm w-36" />
            </div>
            <div className="flex gap-2 shrink-0">
              <button onClick={handleSearch} className={`px-4 py-2 ${primaryBtnClasses} text-sm font-semibold rounded-lg`}>
                Search
              </button>
              <button onClick={handleReset} className="px-4 py-2 bg-gray-100 text-gray-700 text-sm font-semibold rounded-lg hover:bg-gray-200">
                Reset
              </button>
            </div>
          </div>
        )}
      />

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 mt-4 text-sm text-red-700">{error}</div>
      )}

      <div className="bg-white shadow-lg rounded-xl border border-gray-200 overflow-hidden mt-6">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[900px]">
            <thead className="bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200">
              <tr className="text-left text-xs font-semibold text-gray-600 uppercase tracking-wide">
                <th className="px-4 py-3">
                  <SortHeader field="name" label="Name" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} />
                </th>
                <th className="px-4 py-3">Contact</th>
                <th className="px-4 py-3">Type</th>
                <th className="px-4 py-3">Location</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">
                  <SortHeader field="created" label="Created" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} />
                </th>
                <th className="px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-400">Loading…</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-400">No accounts found.</td></tr>
              ) : (
                rows.map((row, idx) => (
                  <tr key={`${row.kind}-${row.id}`} className={`hover:bg-gray-50 ${idx % 2 === 1 ? "bg-gray-50/60" : ""}`}>
                    <td className="px-4 py-3 font-medium text-gray-900">
                      <div className="flex items-center gap-3">
                        <span
                          className={`shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold ${
                            row.kind === "sales_rep"
                              ? "bg-purple-100 text-purple-700"
                              : "bg-blue-100 text-blue-700"
                          }`}
                        >
                          {initials(row.name)}
                        </span>
                        <div>
                          {toTitleCase(row.name) || "—"}
                          {row.title && <div className="text-xs text-gray-500 font-normal">{row.title}</div>}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-gray-600">
                      <div className="flex items-center gap-1">
                        <span>{row.email}</span>
                        <CopyButton value={row.email} label="email" />
                      </div>
                      {row.username && (
                        <div className="text-xs text-gray-400 flex items-center gap-1">
                          <AtSign className="w-3 h-3 shrink-0" />
                          <span>{row.username}</span>
                          <CopyButton value={row.username} label="username" />
                        </div>
                      )}
                      {row.phone && (
                        <div className="text-xs text-gray-400 flex items-center gap-1">
                          <Phone className="w-3 h-3 shrink-0" />
                          <span>{row.phone}</span>
                          <CopyButton value={row.phone} label="phone" />
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap"><TypeBadge userType={row.user_type} kind={row.kind} /></td>
                    <td className="px-4 py-3 text-gray-600">
                      <div className="flex items-center gap-1">
                        <MapPin className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                        {[toTitleCase(row.county_name), toTitleCase(row.state_name) || stateNameById[String(row.state)] || row.state]
                          .filter(Boolean)
                          .join(", ") || "—"}
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap"><StatusBadge isActive={row.is_active} /></td>
                    <td className="px-4 py-3 text-gray-500 whitespace-nowrap">{row.created ? new Date(row.created).toLocaleDateString() : "—"}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {(row.actions || []).map((actionKey) => {
                          const def = ACTION_DEFS[actionKey];
                          if (!def) {
                            console.warn("Unmapped action key from /admin/users/combined:", actionKey, "row:", row.kind, row.id, "full actions[]:", row.actions);
                            return null;
                          }
                          const Icon = def.icon;
                          return (
                            <button
                              key={actionKey}
                              title={def.label}
                              onClick={() => def.onClick(row)}
                              className={`p-1.5 rounded hover:bg-gray-100 ${def.color}`}
                            >
                              <Icon className="w-4 h-4" />
                            </button>
                          );
                        })}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100 text-sm text-gray-600">
          <div className="flex items-center gap-1.5">
            <Users className="w-4 h-4" /> {meta.total} total
          </div>
          <div className="flex items-center gap-3">
            <label className="text-sm font-medium text-gray-700">Rows per page:</label>
            <select
              value={pageSize}
              onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
              className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm bg-white focus:ring-2 focus:ring-[var(--role-border-primary)] focus:border-[var(--role-border-primary)] transition-colors"
            >
              {[10, 25, 50].map((n) => (<option key={n} value={n}>{n}</option>))}
            </select>
          </div>
          <div className="flex items-center gap-2">
            <button
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="px-3 py-1.5 rounded border border-gray-300 disabled:opacity-40"
            >
              Prev
            </button>
            <span>Page {meta.page || page} of {meta.totalPages || 1}</span>
            <button
              disabled={page >= (meta.totalPages || 1)}
              onClick={() => setPage((p) => p + 1)}
              className="px-3 py-1.5 rounded border border-gray-300 disabled:opacity-40"
            >
              Next
            </button>
          </div>
        </div>
      </div>

      {confirmModal.type && confirmModal.row && (
        <Modal title="Confirm" onClose={() => setConfirmModal({ type: null, row: null })}>
          <p className="text-gray-700 mb-4">
            {CONFIRM_COPY[confirmModal.type]?.(confirmModal.row)}
            <br />
            <span className="text-sm text-gray-500">{confirmModal.row.name}</span>
          </p>
          <div className="flex justify-end gap-3">
            <button
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-medium"
              onClick={() => setConfirmModal({ type: null, row: null })}
              disabled={actionBusy}
            >
              Cancel
            </button>
            <button
              className={`px-4 py-2 rounded-lg font-medium text-white ${confirmModal.type === "delete" || confirmModal.type === "reject" ? "bg-red-600 hover:bg-red-700" : "bg-blue-600 hover:bg-blue-700"} disabled:opacity-60`}
              onClick={() => runAction(confirmModal.type, confirmModal.row)}
              disabled={actionBusy}
            >
              {actionBusy ? "Working…" : "Confirm"}
            </button>
          </div>
        </Modal>
      )}

      <Toast message={toast.message} type={toast.type} visible={toast.visible} />
    </div>
  );
}
